import heroImage from "@assets/generated_images/padel_match_action_shot.png";

const BG = "#0d1a0a";
const GREEN = "#325e22";
const GREEN_LIGHT = "#6fcf54";

interface HeroSectionProps {
  onGetStarted?: () => void;
  onLearnMore?: () => void;
}

export function HeroSection({ onGetStarted }: HeroSectionProps) {
  return (
    <section
      className="relative"
      style={{ minHeight: "calc(100vh - 64px)", background: BG, overflow: "clip" }}
    >
      {/* Photo background */}
      <img
        src={heroImage}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "center 30%", opacity: 0.38 }}
      />
      {/* Gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(140deg, rgba(0,0,0,0.05) 0%, rgba(13,26,10,0.6) 35%, rgba(13,26,10,0.97) 75%, #0d1a0a 100%)",
        }}
      />

      {/* Main content */}
      <div
        className="relative z-10 flex items-center w-full px-8 lg:px-16"
        style={{ minHeight: "calc(100vh - 64px - 56px)", paddingTop: 48, paddingBottom: 48 }}
      >
        {/* Left copy */}
        <div className="flex-shrink-0 w-full lg:w-[520px] text-white">
          <h1
            className="font-black text-white leading-none mb-5"
            style={{ fontSize: "clamp(48px, 5.5vw, 72px)", letterSpacing: -3 }}
          >
            Tu pádel,
            <br />
            <span style={{ color: GREEN_LIGHT }}>en serio.</span>
          </h1>

          <p className="text-lg mb-10 max-w-md" style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.6 }}>
            Rankings ELO en tiempo real, ligas privadas, partidos agendados y
            comunidad de jugadores — todo en una app.
          </p>

          {/* Stats row */}
          <div className="flex gap-8 mb-10">
            {[
              ["Rankings ELO", "Tiempo real"],
              ["Ligas", "Ilimitadas"],
              ["Jugadores", "RD & más"],
            ].map(([label, val]) => (
              <div key={label}>
                <div
                  className="text-xs font-bold uppercase tracking-wide"
                  style={{ color: GREEN_LIGHT }}
                >
                  {val}
                </div>
                <div className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                  {label}
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-3">
            <StoreBadge store="apple" />
            <StoreBadge store="google" />
            <button
              onClick={onGetStarted}
              className="text-sm transition-colors"
              style={{ color: "rgba(255,255,255,0.4)", marginLeft: 8, background: "none", border: "none", cursor: "pointer" }}
              data-testid="button-get-started"
            >
              o entra desde la web →
            </button>
          </div>
        </div>

        {/* Right: phone mockup — only shown on large screens */}
        <div className="hidden lg:flex flex-1 items-center justify-center relative">
          <PhoneMockup />
        </div>
      </div>

      {/* Feature strip pinned to bottom of hero */}
      <div
        className="absolute bottom-0 left-0 right-0 z-10 flex"
        style={{
          borderTop: "1px solid rgba(255,255,255,0.07)",
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(12px)",
        }}
      >
        {[
          ["🏆", "Rankings ELO"],
          ["📅", "Partidos agendados"],
          ["🏟️", "Ligas privadas y públicas"],
          ["👥", "Comunidad"],
          ["📊", "Tu progreso"],
        ].map(([icon, label], i) => (
          <div
            key={label}
            className="flex-1 flex items-center justify-center gap-2 py-4"
            style={{
              borderRight: i < 4 ? "1px solid rgba(255,255,255,0.07)" : "none",
            }}
          >
            <span className="text-base">{icon}</span>
            <span className="text-xs font-medium hidden sm:block" style={{ color: "rgba(255,255,255,0.65)" }}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

const APPLE_STORE_URL = "https://apps.apple.com/app/courtvibe/id6744892298";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.courtvibe";

function StoreBadge({ store }: { store: "apple" | "google" }) {
  const isApple = store === "apple";
  return (
    <a
      href={isApple ? APPLE_STORE_URL : PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2.5 rounded-xl no-underline transition-opacity hover:opacity-90"
      style={{
        background: isApple ? "#ffffff" : "rgba(255,255,255,0.08)",
        border: isApple ? "none" : "1px solid rgba(255,255,255,0.18)",
        padding: "10px 18px",
        boxShadow: isApple ? "0 4px 20px rgba(0,0,0,0.3)" : "none",
      }}
    >
      {isApple ? (
        <svg width="19" height="19" viewBox="0 0 24 24" fill="#000">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      ) : (
        <svg width="19" height="19" viewBox="0 0 24 24">
          <path d="M3.18 1.55C2.78 1.98 2.5 2.65 2.5 3.55v16.9c0 .9.28 1.57.68 2l.11.1 9.46-9.46v-.23L3.29 3.45l-.11.1z" fill="#4285F4" />
          <path d="M15.9 16.36l-3.15-3.15v-.22l3.15-3.15.07.04 3.74 2.12c1.07.61 1.07 1.6 0 2.21l-3.74 2.12-.07.03z" fill="#FBBC04" />
          <path d="M15.97 16.33L12.75 13.1 3.18 22.67c.35.37.93.42 1.58.06l11.21-6.4" fill="#EA4335" />
          <path d="M15.97 9.67L4.76 3.27c-.65-.36-1.23-.31-1.58.06l9.57 9.57 3.22-3.23z" fill="#34A853" />
        </svg>
      )}
      <div>
        <div className="text-[10px] leading-none" style={{ color: isApple ? "#888" : "rgba(255,255,255,0.5)" }}>
          Descarga en
        </div>
        <div className="text-sm font-bold leading-tight mt-0.5" style={{ color: isApple ? "#000" : "#fff" }}>
          {isApple ? "App Store" : "Google Play"}
        </div>
      </div>
    </a>
  );
}

function PhoneMockup() {
  return (
    <div className="relative">
      {/* Glow */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: "radial-gradient(ellipse at center, rgba(50,94,34,0.35) 0%, transparent 70%)",
          transform: "scale(1.4)",
        }}
      />
      {/* Phone shell */}
      <div
        className="relative overflow-hidden"
        style={{
          width: 240,
          height: 480,
          borderRadius: 36,
          background: "linear-gradient(160deg, #1e2e1a, #0d1a0a)",
          border: "2px solid rgba(255,255,255,0.12)",
          boxShadow: "0 40px 100px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.08)",
          transform: "rotate(-4deg)",
        }}
      >
        {/* Notch */}
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2"
          style={{ width: 72, height: 5, background: "rgba(255,255,255,0.15)", borderRadius: 3 }}
        />
        {/* Screen content */}
        <div className="absolute inset-0 flex flex-col" style={{ padding: "32px 16px 20px" }}>
          {/* App bar */}
          <div className="flex items-center gap-2 mb-5">
            <div style={{ width: 24, height: 24, borderRadius: 6, background: GREEN, flexShrink: 0 }} />
            <div style={{ height: 4, width: 60, background: "rgba(255,255,255,0.3)", borderRadius: 2 }} />
          </div>
          {/* ELO card */}
          <div
            className="rounded-2xl p-4 mb-3"
            style={{ background: "rgba(50,94,34,0.3)", border: "1px solid rgba(111,207,84,0.2)" }}
          >
            <div className="text-[9px] font-bold uppercase tracking-wide mb-1.5" style={{ color: GREEN_LIGHT }}>
              Tu rating ELO
            </div>
            <div className="font-black text-white" style={{ fontSize: 24, letterSpacing: -1 }}>
              1 <span style={{ color: GREEN_LIGHT }}>842</span>
            </div>
            <div className="mt-2.5 rounded-full overflow-hidden" style={{ height: 4, background: "rgba(255,255,255,0.08)" }}>
              <div
                className="h-full rounded-full"
                style={{ width: "72%", background: `linear-gradient(90deg, ${GREEN}, ${GREEN_LIGHT})` }}
              />
            </div>
          </div>
          {/* Match rows */}
          {["+18", "+12"].map((pts, i) => (
            <div
              key={i}
              className="flex items-center gap-2 rounded-xl mb-2"
              style={{ background: "rgba(255,255,255,0.05)", padding: "9px 10px" }}
            >
              <div
                style={{
                  width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                  background: i === 0 ? GREEN : "rgba(255,255,255,0.1)",
                }}
              />
              <div className="flex-1">
                <div style={{ height: 3, background: "rgba(255,255,255,0.3)", borderRadius: 2, width: "75%", marginBottom: 4 }} />
                <div style={{ height: 3, background: "rgba(255,255,255,0.12)", borderRadius: 2, width: "55%" }} />
              </div>
              <div className="text-[10px] font-bold" style={{ color: GREEN_LIGHT }}>{pts}</div>
            </div>
          ))}
          {/* Bottom nav */}
          <div
            className="mt-auto flex justify-around pt-3"
            style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
          >
            {["⚡", "📋", "➕", "👥", "👤"].map((icon, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <span style={{ fontSize: 13, opacity: i === 0 ? 1 : 0.35 }}>{icon}</span>
                {i === 0 && (
                  <div style={{ width: 3, height: 3, borderRadius: "50%", background: GREEN_LIGHT }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
